import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { Reducer } from 'redux';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { TAppActions } from '../rootDuck';
import { put, takeLatest, call } from 'redux-saga/effects';

import { ActionsUnion, createAction } from '../../utils/action-helper';
import { IServerResponse } from '../../interfaces/server';
import { sendInvite } from '../../crud/users.crud';
import { IUser } from '../../interfaces/user';
import { getResponseMessage } from '../../utils/utils';

const FETCH_SUCCESS = 'users/FETCH_SUCCESS';

const FETCH_BY_ID_SUCCESS = 'users/FETCH_BY_ID_SUCCESS';

const CLEAR_EDIT = 'users/CLEAR_EDIT';
const EDIT_SUCCESS = 'users/EDIT_SUCCESS';

const CLEAR_SEND_INVITE = 'users/CLEAR_SEND_INVITE';
const SEND_INVITE_REQUEST = 'users/SEND_INVITE_REQUEST';
const SEND_INVITE_SUCCESS = 'users/SEND_INVITE_SUCCESS';
const SEND_INVITE_FAIL = 'users/SEND_INVITE_FAIL';

const CLEAR_DEL_USER = 'users/CLEAR_DEL_USER';
const CLEAR_PAGE = 'users/CLEAR_PAGE';

export interface IInitialState {
  page: number;
  per_page: number;
  total: number;
  users: IUser[];
  loading: boolean;
  success: boolean;
  error: string | null;

  user: IUser | null;
  byIdLoading: boolean;
  byIdSuccess: boolean;

  editLoading: boolean;
  editSuccess: boolean;
  editError: string | null;

  sendInviteLoading: boolean;
  sendInviteSuccess: boolean;
  sendInviteError: string | null;

  delUserLoading: boolean;
  delUserSuccess: boolean;
  delUserError: string | null;
}

const initialState: IInitialState = {
  page: 1,
  per_page: 20,
  total: 0,
  users: [],
  loading: false,
  success: false,
  error: null,

  user: null,
  byIdLoading: false,
  byIdSuccess: false,

  editLoading: false,
  editSuccess: false,
  editError: null,

  sendInviteLoading: false,
  sendInviteSuccess: false,
  sendInviteError: null,

  delUserLoading: false,
  delUserSuccess: false,
  delUserError: null,
};

export const reducer: Reducer<IInitialState & PersistPartial, TAppActions> = persistReducer(
  { storage, key: 'users', whitelist: ['user', 'authToken'] },
  (state = initialState, action) => {
    switch (action.type) {
      case FETCH_SUCCESS: {
        return {
          ...state,
          page: action.payload.page,
          per_page: action.payload.per_page,
          total: action.payload.total,
          users: action.payload.data,
          loading: false,
          success: true,
        };
      }

      case CLEAR_PAGE: {
        return {
          ...state,
          page: 1,
          per_page: 20,
          total: 0,
        };
      }

      case FETCH_BY_ID_SUCCESS: {
        return { ...state, user: action.payload.data, byIdLoading: false, byIdSuccess: true };
      }

      case CLEAR_EDIT: {
        return {
          ...state,
          editLoading: false,
          editError: null,
          editSuccess: false,
          user: null,
        };
      }

      case EDIT_SUCCESS: {
        return { ...state, editLoading: false, editSuccess: true, user: action.payload };
      }

      case CLEAR_SEND_INVITE: {
        return {
          ...state,
          sendInviteLoading: false,
          sendInviteError: null,
          sendInviteSuccess: false,
        };
      }

      case SEND_INVITE_REQUEST: {
        return {
          ...state,
          sendInviteLoading: true,
          sendInviteError: null,
          sendInviteSuccess: false,
        };
      }

      case SEND_INVITE_SUCCESS: {
        return { ...state, sendInviteLoading: false, sendInviteSuccess: true };
      }

      case SEND_INVITE_FAIL: {
        return { ...state, sendInviteLoading: false, sendInviteError: action.payload };
      }

      case CLEAR_DEL_USER: {
        return { ...state, delUserLoading: false, delUserError: null, delUserSuccess: false };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  fetchSuccess: (payload: IServerResponse<IUser[]>) => createAction(FETCH_SUCCESS, payload),
  clearPage: () => createAction(CLEAR_PAGE),
  fetchByIdSuccess: (payload: IServerResponse<IUser>) => createAction(FETCH_BY_ID_SUCCESS, payload),

  clearEdit: () => createAction(CLEAR_EDIT),
  editSuccess: (data: IUser) => createAction(EDIT_SUCCESS, data),

  clearSendInvite: () => createAction(CLEAR_SEND_INVITE),
  sendInviteRequest: (payload: string) => createAction(SEND_INVITE_REQUEST, payload),
  sendInviteSuccess: () => createAction(SEND_INVITE_SUCCESS),
  sendInviteFail: (payload: string) => createAction(SEND_INVITE_FAIL, payload),

  clearDelUser: () => createAction(CLEAR_DEL_USER),
};

export type TActions = ActionsUnion<typeof actions>;

function* sendInviteSaga({ payload }: { payload: string }) {
  try {
    yield call(() => sendInvite(payload));
    yield put(actions.sendInviteSuccess());
  } catch (e) {
    yield put(actions.sendInviteFail(getResponseMessage(e)));
  }
}

export function* saga() {
  yield takeLatest<ReturnType<typeof actions.sendInviteRequest>>(
    SEND_INVITE_REQUEST,
    sendInviteSaga
  );
}
